<template>
  <div
    class="w-full flex flex-col items-center justify-center min-h-[600px] py-12 px-4 relative"
  >
    <!-- Progress Indicator -->
    <div class="w-full max-w-2xl mb-12 relative">
      <div
        class="absolute top-1/2 left-0 w-full h-0.5 bg-gray-200 dark:bg-gray-800 -translate-y-1/2"
      ></div>
      <div class="flex justify-between relative z-10">
        <div
          v-for="step in [1, 2, 3, 4]"
          :key="step"
          class="flex flex-col items-center gap-3"
        >
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 border-2"
            :class="{
              'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/30 scale-110':
                currentStep === step,
              'bg-emerald-500 border-emerald-500 text-white':
                currentStep > step,
              'bg-white dark:bg-[#050a0c] border-gray-300 dark:border-gray-700 text-gray-400':
                currentStep < step,
            }"
          >
            <i v-if="currentStep > step" class="fas fa-check text-xs"></i>
            <span v-else>{{ step }}</span>
          </div>
          <span
            class="text-[10px] font-bold uppercase tracking-wider transition-colors duration-300"
            :class="
              currentStep === step
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-gray-400'
            "
          >
            {{ ["Concept", "Validate", "Questions", "Explore"][step - 1] }}
          </span>
        </div>
      </div>
    </div>

    <!-- Step 1: Initial Concept -->
    <div
      v-if="currentStep === 1"
      class="w-full max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div
        class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-200 dark:border-gray-800 shadow-2xl rounded-3xl p-8 text-center"
      >
        <div
          class="w-20 h-20 bg-blue-600/10 dark:bg-blue-400/10 rounded-2xl flex items-center justify-center mx-auto mb-6"
        >
          <i
            class="fas fa-lightbulb-on text-3xl text-blue-600 dark:text-blue-400"
          ></i>
        </div>
        <h2
          class="text-3xl font-headline font-bold text-gray-900 dark:text-white mb-4"
        >
          Let's build your Research Compass
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
          What area of research or technology would you like to explore today?
          Start with a broad concept or a specific question.
        </p>

        <div class="relative group">
          <textarea
            ref="initialConcept"
            v-model="initialConcept"
            class="w-full bg-gray-50 dark:bg-[#050a0c] border-2 border-gray-200 dark:border-gray-800 rounded-2xl px-6 py-5 text-lg focus:border-blue-600 dark:focus:border-blue-500 focus:ring-0 transition-all min-h-[120px] resize-none"
            placeholder="e.g., The impact of carbon capture technology on urban air quality..."
            @keydown.enter.prevent="saveConcept"
          />
          <div class="mt-6 flex justify-center">
            <button
              class="group relative inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-xl hover:shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!initialConcept || initialConcept.length < 5"
              @click="saveConcept"
            >
              <span>Continue</span>
              <i
                class="fas fa-arrow-right group-hover:translate-x-1 transition-transform"
              ></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: Validate & Refine Concepts -->
    <div
      v-if="currentStep === 2"
      class="w-full max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div
        class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-200 dark:border-gray-800 shadow-2xl rounded-3xl p-8"
      >
        <div class="flex items-center gap-4 mb-8">
          <div
            class="w-12 h-12 bg-blue-600/10 dark:bg-blue-400/10 rounded-xl flex items-center justify-center shrink-0"
          >
            <i
              class="fas fa-sparkles text-xl text-blue-600 dark:text-blue-400"
            ></i>
          </div>
          <div>
            <h2
              class="text-2xl font-headline font-bold text-gray-900 dark:text-white"
            >
              Refining your concept
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Select the specific areas you'd like to include in your search.
            </p>
          </div>
        </div>

        <div v-if="isValidatingConcept" class="py-12 text-center">
          <i
            class="fas fa-circle-notch fa-spin text-4xl text-blue-600 mb-4"
          ></i>
          <p class="font-bold text-gray-600 dark:text-gray-400 italic">
            Analyzing concept depth...
          </p>
        </div>

        <div v-else class="space-y-6">
          <div
            v-if="researchCompass.conceptValidationResult === false"
            class="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5 flex gap-4"
          >
            <i class="fas fa-exclamation-triangle text-amber-500 mt-1"></i>
            <div>
              <p
                class="text-sm font-bold text-amber-800 dark:text-amber-400 mb-1"
              >
                Your concept is quite broad
              </p>
              <p class="text-xs text-amber-700/80 dark:text-amber-400/70">
                {{ researchCompass.conceptValidationReasoning }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              v-for="concept in researchCompass.suggestedConcepts"
              :key="concept.concept"
              class="flex items-start gap-3 p-4 rounded-2xl border-2 transition-all text-left group"
              :class="
                concept.selected
                  ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                  : 'bg-gray-50 dark:bg-[#050a0c] border-gray-100 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:border-blue-500/30'
              "
              @click="toggleConcept(concept)"
            >
              <div
                class="w-5 h-5 rounded-md border flex items-center justify-center shrink-0 mt-0.5"
                :class="
                  concept.selected
                    ? 'bg-white border-white text-blue-600'
                    : 'bg-white dark:bg-black border-gray-300 dark:border-gray-700 text-transparent'
                "
              >
                <i class="fas fa-check text-[10px]"></i>
              </div>
              <span class="text-sm font-bold leading-tight">{{
                concept.concept
              }}</span>
            </button>
          </div>

          <div class="pt-6 flex justify-between items-center">
            <button
              class="text-gray-500 dark:text-gray-400 font-bold text-sm hover:text-blue-600"
              @click="prev"
            >
              <i class="fas fa-arrow-left mr-2"></i> Back
            </button>
            <button
              class="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold shadow-lg transition-all"
              @click="saveSelectedConcepts"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Choose Research Questions -->
    <div
      v-if="currentStep === 3"
      class="w-full max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div
        class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-200 dark:border-gray-800 shadow-2xl rounded-3xl p-8"
      >
        <div class="flex items-center gap-4 mb-8">
          <div
            class="w-12 h-12 bg-blue-600/10 dark:bg-blue-400/10 rounded-xl flex items-center justify-center shrink-0"
          >
            <i
              class="fas fa-list-check text-xl text-blue-600 dark:text-blue-400"
            ></i>
          </div>
          <div>
            <h2
              class="text-2xl font-headline font-bold text-gray-900 dark:text-white"
            >
              Choose your research questions
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Select the questions you'd like to investigate in this compass.
            </p>
          </div>
        </div>

        <div v-if="loadingResearchCompassQuestions" class="py-12 text-center">
          <i
            class="fas fa-circle-notch fa-spin text-4xl text-blue-600 mb-4"
          ></i>
          <p class="font-bold text-gray-600 dark:text-gray-400 italic">
            Synthesizing research pathways...
          </p>
        </div>

        <div v-else class="space-y-4">
          <div
            v-for="question in researchCompass.suggestedQuestions"
            :key="question.question"
            class="group p-4 rounded-2xl border-2 transition-all cursor-pointer flex gap-4"
            :class="
              question.selected
                ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                : 'bg-gray-50 dark:bg-[#050a0c] border-gray-100 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:border-blue-500/30'
            "
            @click="toggleQuestion(question)"
          >
            <div
              class="w-6 h-6 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
              :class="
                question.selected
                  ? 'bg-white border-white text-blue-600'
                  : 'bg-white dark:bg-black border-gray-300 dark:border-gray-700 text-transparent'
              "
            >
              <i class="fas fa-check text-xs"></i>
            </div>
            <p class="text-sm font-bold leading-relaxed">
              {{ question.question }}
            </p>
          </div>

          <div class="pt-8 flex justify-between items-center">
            <button
              class="text-gray-500 dark:text-gray-400 font-bold text-sm hover:text-blue-600"
              @click="prev"
            >
              <i class="fas fa-arrow-left mr-2"></i> Back
            </button>
            <button
              class="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold shadow-lg transition-all disabled:opacity-50"
              :disabled="selectedQuestionLength === 0"
              @click="saveSelectedQuestions"
            >
              Create Research Compass
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 4: Loading / Transition to Wizard -->
    <div
      v-if="currentStep === 4"
      class="w-full max-w-2xl animate-in fade-in duration-500 text-center"
    >
      <div
        class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-200 dark:border-gray-800 shadow-2xl rounded-3xl p-12"
      >
        <div class="relative w-32 h-32 mx-auto mb-8">
          <div
            class="absolute inset-0 bg-blue-600/20 rounded-full animate-ping"
          ></div>
          <div
            class="relative w-full h-full bg-blue-600 rounded-full flex items-center justify-center shadow-2xl"
          >
            <i class="fas fa-compass text-white text-5xl animate-spin-slow"></i>
          </div>
        </div>
        <h2
          class="text-3xl font-headline font-bold text-gray-900 dark:text-white mb-4"
        >
          Initializing Compass
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-12">
          We're gathering sources and analyzing the landscape for your chosen
          questions.
        </p>

        <button
          class="bg-blue-600 hover:bg-blue-700 text-white px-12 py-4 rounded-2xl font-bold text-lg shadow-xl shadow-blue-500/20 transition-all flex items-center gap-3 mx-auto"
          @click="beginHydration"
        >
          <span>Enter Research Compass</span>
          <i class="fas fa-rocket"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import * as utils from "../../utils";

const { keysToCamel, keysToSnake, resolveRcApiUrl } = utils;

export default {
  components: {},
  props: {
    initialQuestionDetail: Object,
  },
  data() {
    return {
      currentStep: 1,
      initialConcept: "",
      researchCompass: {},
      questionDetail: this.initialQuestionDetail,
      isLoading: false,
      isValidatingConcept: false,
      loadingResearchCompass: false,
      loadingResearchCompassQuestions: false,
      loadingResearchCompassConcepts: false,
      frequencyPercentage: 0.15,
      paperOrderPercentage: 0.15,
      influentialCitationCountPercentage: 0.15,
      q1JournalCountPercentage: 0.55,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    };
  },
  computed: {
    isPublic() {
      return Boolean(this.$route.params.viewKey);
    },
    activeAxios() {
      if (this.isPublic) {
        return this.$api.axiosPublic;
      }
      return this.$api.axios;
    },
    rcApiUrl() {
      const baseUrl = `/research-compass/${
        this.researchCompass?.hashedId ||
        this.$route.params.researchCompassId ||
        ""
      }/`;
      const params = new URLSearchParams();
      if (this.$route.params.collaborationSpaceId) {
        params.append(
          "collaboration_space_id",
          this.$route.params.collaborationSpaceId,
        );
      }
      if (this.$route.params.viewKey) {
        params.append("view_key", this.$route.params.viewKey);
      }
      const queryString = params.toString();
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    },
    selectedQuestionLength() {
      return (
        this.researchCompass.suggestedQuestions?.filter(
          (question) => question.selected,
        ).length || 0
      );
    },
    selectedConceptLength() {
      return (
        this.researchCompass.suggestedConcepts?.filter(
          (concept) => concept.selected,
        ).length || 0
      );
    },
  },
  watch: {
    currentStep(newValue) {
      if (newValue === 1) {
        nextTick(() => {
          if (this.$refs.initialConcept) this.$refs.initialConcept.focus();
        });
      }
    },
  },
  mounted() {
    if (this.$route.params.researchCompassId) {
      this.loadingResearchCompass = true;
      this.loadResearchCompass();
    }
    if (this.$posthog) this.$posthog.capture("ResearchCompassIdeation");
  },
  methods: {
    next() {
      this.currentStep++;
    },
    prev() {
      this.currentStep--;
    },
    goToStep(step) {
      this.currentStep = step;
    },
    loadResearchCompass(evaluateStep = true) {
      const questionDetailId = this.questionDetail?.hashedId;
      const url = this.rcApiUrl;
      this.activeAxios.get(url).then((resp) => {
        const data = keysToCamel(resp.data);
        this.researchCompass = data;
        this.initialConcept = this.researchCompass.title;
        if (evaluateStep) {
          this.goToStep(this.evaluateMaxStep());
        }
        this.loadingResearchCompass = false;
        if (questionDetailId) {
          this.questionDetail = this.researchCompass.questions.find(
            (question) => question.hashedId === questionDetailId,
          );
        }
      });
    },
    evaluateMaxStep() {
      if (!this.initialConcept) {
        return 1;
      }
      if (
        !this.researchCompass?.suggestedConcepts ||
        (this.researchCompass.conceptValidationResult === false &&
          this.selectedConceptLength === 0)
      ) {
        return 2;
      }
      if (
        !this.researchCompass?.suggestedQuestions ||
        this.selectedQuestionLength === 0
      ) {
        return 3;
      }
      return 4;
    },
    saveConcept() {
      if (this.researchCompass?.hashedId) {
        const url = this.rcApiUrl;
        this.activeAxios
          .put(
            url,
            keysToSnake({
              title: this.initialConcept,
            }),
          )
          .then((resp) => {
            const data = keysToCamel(resp.data);
            this.researchCompass = data;
            this.next();
            this.validateConcept();
          });
      } else {
        const baseUrl = `/research-compass/`;
        const params = new URLSearchParams();
        if (this.$route.params.collaborationSpaceId) {
          params.append(
            "collaboration_space_id",
            this.$route.params.collaborationSpaceId,
          );
        }
        const url = params.toString()
          ? `${baseUrl}?${params.toString()}`
          : baseUrl;

        this.activeAxios
          .post(
            url,
            keysToSnake({
              title: this.initialConcept,
              conceptOrQuestion: this.initialConcept,
            }),
          )
          .then((resp) => {
            const data = keysToCamel(resp.data);
            this.researchCompass = data;
            this.next();
            this.validateConcept();
          });
      }
    },
    getSuggestedQuestions() {
      this.loadingResearchCompassQuestions = true;
      const url = resolveRcApiUrl(
        this.researchCompass.hashedId,
        "suggested-question",
        this.$route.params,
      );
      this.activeAxios.get(url, {}).then((resp) => {
        const data = keysToCamel(resp.data);
        this.researchCompass = data;
        this.loadingResearchCompassQuestions = false;
      });
    },
    getSuggestedConcepts() {
      this.loadingResearchCompassConcepts = true;
      const url = resolveRcApiUrl(
        this.researchCompass.hashedId,
        "suggested-concept",
        this.$route.params,
      );
      this.activeAxios.get(url, {}).then((resp) => {
        const data = keysToCamel(resp.data);
        this.researchCompass = data;
        this.loadingResearchCompassConcepts = false;
      });
    },
    validateConcept() {
      this.isValidatingConcept = true;
      const url = resolveRcApiUrl(
        this.researchCompass.hashedId,
        "validate-concept",
        this.$route.params,
      );
      this.activeAxios.get(url, {}).then((resp) => {
        const data = keysToCamel(resp.data);
        this.researchCompass = data;
        this.isValidatingConcept = false;
      });
    },
    toggleConcept(item) {
      item.selected = !item.selected;
      const url = resolveRcApiUrl(
        this.researchCompass.hashedId,
        "suggested-concept",
        this.$route.params,
      );
      this.activeAxios
        .post(
          url,
          keysToSnake({
            concept: item.concept,
            selected: item.selected,
          }),
        )
        .then((resp) => {
          const data = keysToCamel(resp.data);
          this.researchCompass = data;
        });
    },
    toggleQuestion(item) {
      item.selected = !item.selected;
      const url = resolveRcApiUrl(
        this.researchCompass.hashedId,
        "suggested-question",
        this.$route.params,
      );
      this.activeAxios
        .post(
          url,
          keysToSnake({
            question: item.question,
            selected: item.selected,
          }),
        )
        .then((resp) => {
          const data = keysToCamel(resp.data);
          this.researchCompass = data;
        });
    },
    saveSelectedConcepts() {
      const url = resolveRcApiUrl(
        this.researchCompass.hashedId,
        "save-concepts",
        this.$route.params,
      );
      this.activeAxios.post(url).then((resp) => {
        const data = keysToCamel(resp.data);
        this.next();
        this.researchCompass = data;
        this.getSuggestedQuestions();
      });
    },
    saveSelectedQuestions() {
      const url = resolveRcApiUrl(
        this.researchCompass.hashedId,
        "save-questions",
        this.$route.params,
      );
      this.activeAxios.post(url).then((resp) => {
        const data = keysToCamel(resp.data);
        this.next();
        this.researchCompass = data;
      });
    },
    beginHydration() {
      this.researchCompass.questions.forEach((question) => {
        const qUrl = `/research-compass/question/${question.hashedId}/hydrate/${
          this.rcApiUrl.includes("?") ? `?${this.rcApiUrl.split("?")[1]}` : ""
        }`;
        this.activeAxios.post(qUrl, {}).then(() => {
          this.$emit("close-ideation-modal");
        });
      });
    },
  },
};
</script>
